import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fadeUp } from "../animations/variants";
import { publicClient } from "../configs/axiosConfig"; // ✅ direct, no circular deps
import { Eye, Globe, LinkedinIcon } from "lucide-react";
import { CreditCard, Wallet, Smartphone, Banknote } from "lucide-react";
import { useSelector } from "react-redux";
import { footerText } from "../i18n/footer";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import {
  FaCcVisa,
  FaCcMastercard,
  FaApplePay,
  FaGooglePay,
  FaStripe,
} from "react-icons/fa";
import { facebooklink, instagramlink, linkedin } from "./SocialMediaLink";
import { useLocation, useNavigate } from "react-router-dom";
import { menu } from "data/data";

const Footer = () => {
  const [gaStats, setGaStats] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useSelector((state) => state.language.lang);
  const t = footerText[lang];

  useEffect(() => {
    let mounted = true;

    const fetchGaStats = async () => {
      try {
        const res = await publicClient.get("v2/statistic/ga-visitors");
        if (mounted) {
          setGaStats(res.data);
        }
      } catch (err) {
        console.error("Footer GA fetch failed:", err);
      }
    };

    fetchGaStats();

    return () => {
      mounted = false;
    };
  }, []);

  const normalizeCount = (value) => {
    if (typeof value !== "number") return 100;
    return value + 100;
  };

  const normalizeCountryName = (country) => {
    if (!country || country.trim() === "") return "Unknown";
    return country;
  };

  const COUNTRY_CODE_MAP = {
    "New Zealand": "NZ",
    Australia: "AU",
    Vietnam: "VN",

    "United States": "US",
    "United Kingdom": "GB",
    Canada: "CA",

    China: "CN",
    Japan: "JP",
    "South Korea": "KR",
    Thailand: "TH",
    Singapore: "SG",
    Malaysia: "MY",
    Indonesia: "ID",
    Philippines: "PH",

    India: "IN",

    France: "FR",
    Germany: "DE",
    Netherlands: "NL",
    Spain: "ES",
    Italy: "IT",

    Brazil: "BR",
    Mexico: "MX",
    "South Africa": "ZA",
  };

  const isoToFlag = (isoCode) => {
    if (!isoCode || isoCode.length !== 2) return "🌐";

    return isoCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt()),
      );
  };

  const countryToFlag = (country) => {
    if (!country || country.trim() === "") return "🌐";

    const iso = COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();

    return isoToFlag(iso);
  };

  const countryToCode = (country) => {
    if (!country || country.trim() === "") return "UN";

    return COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
  };

  useEffect(() => {
    let mounted = true;
    publicClient
      .get("v2/statistic/ga-visitors")
      .then((res) => mounted && setGaStats(res.data))
      .catch(() => {});
    return () => (mounted = false);
  }, []);

  const normalize = (v) => (typeof v === "number" ? v + 100 : 100);

  const handleScroll = (id) => {
    if (location.pathname !== "/") {
      navigate("/" + id);
      return;
    }
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = () => {
    navigate("/");

    setTimeout(() => {
      const el = document.querySelector("#hero");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
  };

  const goToIndustry = () => {
    if (location.pathname !== "/") {
      navigate("/#latestworkshop");
      return;
    }

    const el = document.querySelector("#latestworkshop");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goToPage = (item) => {
    if (item.label === "Industry Insights & Resources") {
      goToIndustry();
      return;
    }

    navigate(item.path);
  };

  const getLabel = (label) => {
    if (!label) return "";

    if (typeof label === "string") return label;

    if (typeof label === "object") {
      if (typeof label.en === "string" || typeof label.vi === "string") {
        return label?.[lang] || label?.en || "";
      }
    }

    return "";
  };

  return (
    <footer className="bg-black text-white pt-20 pb-10 px-6">
      <div className="max-w-7xl mx-auto">
        {/* TOP */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* COLUMN 1 — BRAND */}
          <div className="space-y-6">
            <img
              src="/maria/logongang_trang.png"
              className="w-40 cursor-pointer"
              onClick={goHome}
            />

            <p className="text-sm text-textGray leading-relaxed">
              Start Growing Your Business Today.
            </p>

            <div className="flex gap-4 text-reddark">
              <a href={facebooklink} target="_blank" rel="noreferrer">
                <Facebook className="w-5 h-5 hover:text-white transition" />
              </a>
              <a href={instagramlink} target="_blank" rel="noreferrer">
                <Instagram className="w-5 h-5 hover:text-white transition" />
              </a>
              <a href={linkedin} target="_blank" rel="noreferrer">
                <LinkedinIcon className="w-5 h-5 hover:text-white transition" />
              </a>
            </div>
          </div>

          {/* COLUMN 2 — ABOUT + SERVICES */}
          <div className="space-y-8">
            {menu
              .filter((m) => ["About", "Services"].includes(m.label.en))
              .map((section, idx) => (
                <div key={idx}>
                  <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                    {getLabel(section.label)}
                  </h4>

                  <ul className="space-y-2">
                    {section.children.map((sub, i) => (
                      <li key={i}>
                        <button
                          onClick={() => navigate(sub.path)}
                          className="text-sm text-textGray hover:text-white transition"
                        >
                          {getLabel(sub.label)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>

          {/* COLUMN 3 — WORKSHOPS + NETWORKS */}
          <div className="space-y-8">
            {menu
              .filter((m) =>
                ["Events", "Networks", "Insights"].includes(m.label.en),
              )
              .map((section, idx) => (
                <div key={idx}>
                  <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                    {getLabel(section.label)}
                  </h4>

                  <ul className="space-y-2">
                    {section.children.map((sub, i) => (
                      <li key={i}>
                        <button
                          onClick={() => navigate(sub.path)}
                          className="text-sm text-textGray hover:text-white transition"
                        >
                          {getLabel(sub.label)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>

          {/* COLUMN 4 — INSIGHTS + CONTACT */}
          <div className="space-y-8">
            {menu
              .filter((m) => ["Community", "Contact"].includes(m.label.en))
              .map((section, idx) => (
                <div key={idx}>
                  <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">
                    {getLabel(section.label)}
                  </h4>

                  <ul className="space-y-2">
                    {section.children.map((sub, i) => (
                      <li key={i}>
                        <button
                          onClick={() => navigate(sub.path)}
                          className="text-sm text-textGray hover:text-white transition"
                        >
                          {getLabel(sub.label)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>

        {/* DIVIDER */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-textGray">
          {/* LEFT */}
          <p>
            © {new Date().getFullYear()} MARIA ADVISORY. {t.copyright}
          </p>

          {/* CENTER (LIVE STATS) */}
          {/* {gaStats && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Eye size={14} />
                <span className="text-reddark">
                  {normalizeCount(gaStats.realtime?.totalUsers)}
                </span>
                <span>{t.online}</span>
              </div>

              <div className="flex items-center gap-1">
                <Globe size={14} />
                <span className="text-reddark">
                  {normalizeCount(gaStats.allTime?.totalUsers)}
                </span>
                <span>{t.visitors}</span>
              </div>
            </div>
          )} */}

          {/* RIGHT */}
          <p>
            {t.designBy}{" "}
            <a
              href="https://miraelite.com/"
              target="_blank"
              rel="noreferrer"
              className="text-reddark hover:underline"
            >
              Mira Elite with ❤️
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
